import { WorkerProfileField } from 'types/profileCustomFields';
import { useContactSectionsConfig } from './useSectionsConfig';
import { useContactForm } from './useContactForm';
import { IContactFormState } from '../types';
import { useAppSelector } from 'modules/state-mgmt/store';

/**
 * Master hook that combines all contact information functionality
 */
interface IUseContactInformationReturn {
  // Configuration
  sectionsConfig: ReturnType<typeof useContactSectionsConfig>;

  // Form data and handlers from useForm
  contactForm: IContactFormState;
  formErrors: Record<string, { message: string; type?: string }>;
  setValue: <K extends keyof IContactFormState>(key: K, value: IContactFormState[K]) => void;
  handleSubmit: (e?: React.FormEvent) => Promise<void>;

  // Field change handlers
  handleFieldChange: (field: WorkerProfileField, value: string) => void;
  handlePhoneChange: (
    field: WorkerProfileField,
  ) => (value: { phone: string; inputValue: string; country: any }) => void;
  handleDirectFieldUpdate: (fieldName: keyof IContactFormState, value: string) => void;

  // Edit permissions
  canEditEmail: boolean;
  canEditMobilePhoneNumber: boolean;

  isSubmitting: boolean;
}

export const useContactInformation = (): IUseContactInformationReturn => {
  // Get hasReachedConsentForm from Redux
  const { hasReachedConsentForm } = useAppSelector(state => ({
    hasReachedConsentForm: state.onboarding.temp?.hasReachedConsentForm || false,
  }));

  // Get form state and handlers with built-in API integration
  const {
    contactForm,
    formErrors,
    setValue,
    handleSubmit,
    handleFieldChange,
    handlePhoneChange,
    handleDirectFieldUpdate,
    canEditEmail,
    canEditMobilePhoneNumber,
    isSubmitting,
  } = useContactForm();

  // Get section configuration with hasReachedConsentForm and form values
  const sectionsConfig = useContactSectionsConfig(hasReachedConsentForm, contactForm);

  return {
    sectionsConfig,
    contactForm,
    formErrors,
    setValue,
    handleSubmit, // Use the form's handleSubmit for UI events
    handleFieldChange,
    handlePhoneChange,
    handleDirectFieldUpdate,
    canEditEmail,
    canEditMobilePhoneNumber,
    isSubmitting,
  };
};
